import { Link } from "@tanstack/react-router";
import { useId, useState } from "react";
import { z } from "zod";
import { alwaysShort, approvalInsetLabel, approvalOutcome, approvalPreview, approvalSentence } from "../../shared/approval-sentence";
import type { ApprovalView } from "../../shared/contracts";
import { useSettings } from "../lib/agent";
import { api, errorText } from "../lib/api";
import { fmtDateTime, fmtRelative, RISK_LABEL } from "../lib/format";
import { useNow } from "../lib/hooks";
import { invalidate, mutateResource } from "../lib/store";
import { AgentAvatarView } from "./avatar";
import { IconCheck, IconX } from "./icons";
import { Button, cx, Notice, Spinner } from "./ui";

const VIA_LABEL: Record<string, string> = {
  web: "depuis l'app",
  telegram: "sur Telegram",
  sms: "par SMS",
  imessage: "par iMessage",
  expiry: "faute de réponse",
};

const editedArgsSchema = z.record(z.string(), z.unknown());

/** Puts a decided/updated approval into every cached list so all screens agree immediately. */
export function applyApprovalUpdate(updated: ApprovalView): void {
  const upsert = (list: ApprovalView[] | undefined) => {
    if (!list) return list;
    const found = list.some((a) => a.id === updated.id);
    return found ? list.map((a) => (a.id === updated.id ? updated : a)) : [updated, ...list];
  };
  mutateResource<ApprovalView[]>("approvals:all", upsert);
  mutateResource<ApprovalView[]>("approvals:pending", (list) =>
    updated.status === "pending" ? upsert(list) : list?.filter((a) => a.id !== updated.id),
  );
  invalidate("approvals:");
  invalidate("dashboard");
  invalidate("pulse-summary");
}


/**
 * One approval (data-testid="approval-card"), in plain words: one sentence saying what the agent wants to do, at
 * most one preview (the words that will be sent, an amount, or a warning), then « Oui » / « Non » — plus « Toujours
 * oui pour … » when the action can become an allow rule. Everything technical (every line of the exact action, the
 * agent's stated reason, the code, the expiry, editing the parameters, a note) sits behind one « Détails ». Once
 * decided, one short line says what happened. `plain` drops the card frame (inside another card); `compact` keeps
 * only the essentials; `onDecided` hears the decision (the home page's character is pleased).
 */
export function ApprovalCard({
  approval,
  compact = false,
  focused = false,
  plain = false,
  deck = false,
  onDecided,
}: {
  approval: ApprovalView;
  compact?: boolean;
  focused?: boolean;
  plain?: boolean;
  /** One card at a time on a phone (ValidationsMobile.dc.html): big type, big buttons. */
  deck?: boolean;
  onDecided?: (updated: ApprovalView) => void;
}) {
  const now = useNow(30_000);
  const [busy, setBusy] = useState<"approve" | "reject" | "always" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [argsText, setArgsText] = useState("");
  const [argsError, setArgsError] = useState<string | null>(null);
  const [note, setNote] = useState("");
  const editorId = useId();
  const noteId = useId();

  const settings = useSettings();
  const agentName = settings.data?.agentName ?? "Ton agent";
  const pending = approval.status === "pending";
  const overdue = pending && approval.expiresAt <= now;
  const editable = !compact && approval.kind === "app_action" && Boolean(approval.action);
  const sentence = approvalSentence(approval);
  const preview = approvalPreview(approval);
  const done = approval.status === "approved" || approval.status === "executed";

  async function decide(decision: "approve" | "reject" | "always", editedArgs?: Record<string, unknown>) {
    if (busy) return;
    setBusy(decision);
    setError(null);
    try {
      const updated = await api("POST /api/approvals/:id/decide", {
        params: { id: approval.id },
        body: {
          decision: decision === "reject" ? "reject" : "approve",
          ...(decision === "always" ? { always: true } : {}),
          ...(editedArgs ? { editedArgs } : {}),
          ...(note.trim() ? { note: note.trim().slice(0, 500) } : {}),
        },
      });
      if (decision === "always") invalidate("rules");
      applyApprovalUpdate(updated);
      setEditing(false);
      onDecided?.(updated);
    } catch (err) {
      setError(errorText(err));
      invalidate("approvals:");
    } finally {
      setBusy(null);
    }
  }

  function approveEdited() {
    let value: unknown;
    try {
      value = JSON.parse(argsText);
    } catch {
      setArgsError("Ce n'est pas du JSON valide.");
      return;
    }
    const parsed = editedArgsSchema.safeParse(value);
    if (!parsed.success || Array.isArray(value)) {
      setArgsError("Les paramètres doivent être un objet JSON ({ … }).");
      return;
    }
    setArgsError(null);
    void decide("approve", parsed.data);
  }

  const quoteInMeta = compact && pending && preview?.kind === "quote";
  const label = approvalInsetLabel(approval);

  // Decided: one faint line (« Message à Léa · Envoyé. »), the details still one tap away.
  if (!pending && !plain) {
    return (
      <article
        data-testid="approval-card"
        id={`approval-${approval.id}`}
        className={cx("cv-in flex min-w-0 scroll-mt-24 flex-col gap-0.5 rounded-[20px] px-4 py-1.5", focused && "bg-elev")}
      >
        <p className="min-w-0 text-[13.5px] text-subtle wrap-anywhere">
          {sentence} · <strong className={cx("font-semibold", done ? "text-fg" : approval.status === "failed" ? "text-danger" : "text-muted")}>{approvalOutcome(approval)}</strong>
        </p>
        <ApprovalDetails approval={approval} agentName={agentName} now={now} compact={compact} pending={false} overdue={false} done={done} note={note} setNote={setNote} noteId={noteId} editable={false} editing={false} onEdit={() => undefined} />
      </article>
    );
  }

  return (
    <article
      data-testid="approval-card"
      id={`approval-${approval.id}`}
      className={cx(
        "cv-in flex min-w-0 scroll-mt-24 flex-col gap-2.5",
        plain ? "" : "rounded-[20px] bg-bubble-agent px-4 py-3.5",
        deck && "min-h-[calc(100dvh-170px)] gap-4 p-5",
        !plain && focused && "shadow-[inset_0_0_0_1.5px_var(--accent)]",
      )}
    >
      <div className="flex min-w-0 items-center gap-2">
        {!compact ? <AgentAvatarView agent={{ name: agentName, avatar: settings.data?.avatar ?? null, character: settings.data?.character ?? null, main: true }} size={24} bare /> : null}
        {quoteInMeta ? (
          <h3 className="min-w-0 text-[13px] text-muted wrap-anywhere">{sentence}</h3>
        ) : (
          <h3 className={cx("min-w-0 wrap-anywhere", deck ? "font-display text-[26px] leading-[1.15]" : "text-[15.5px] leading-snug", !pending && "text-muted")}>
            {sentence}
            {pending ? "\u00a0?" : ""}
          </h3>
        )}
      </div>

      {pending && (preview || label) ? (
        <div className="min-w-0 rounded-[12px] bg-inset px-2.5 py-2">
          {label ? <p className="text-[12.5px] font-semibold text-muted wrap-anywhere">{label}</p> : null}
          {preview?.kind === "quote" ? (
            <blockquote className={cx("mt-0.5 line-clamp-4 leading-snug whitespace-pre-line text-fg wrap-anywhere", deck ? "text-[16px]" : "text-[14px]")}>{preview.text}</blockquote>
          ) : preview?.kind === "amount" ? (
            <p className="mt-0.5 text-[20px] font-semibold tabular-nums">{preview.text}</p>
          ) : preview?.kind === "warning" ? (
            <p className="mt-0.5 text-[14px] text-danger wrap-anywhere">{preview.text}</p>
          ) : null}
        </div>
      ) : null}

      {pending ? (
        <div className={cx("flex flex-col gap-1", deck && "mt-auto")}>
          {editable && editing ? (
            <div className="space-y-2">
              <label htmlFor={editorId} className="block text-[13px] font-medium text-muted">
                Paramètres envoyés à l'app (JSON, même action)
              </label>
              <textarea
                id={editorId}
                value={argsText}
                onChange={(e) => {
                  setArgsText(e.target.value);
                  setArgsError(null);
                }}
                spellCheck={false}
                className="min-h-44 w-full rounded-[14px] border border-line bg-bg p-3 font-mono text-[12.5px] leading-relaxed outline-none focus:border-line-strong focus:ring-4 focus:ring-[var(--glow)]"
                aria-invalid={argsError ? true : undefined}
              />
              {argsError ? (
                <p className="text-[12.5px] text-danger" role="alert">
                  {argsError}
                </p>
              ) : null}
              <div className="flex flex-wrap gap-2">
                <Button size="sm" busy={busy === "approve"} disabled={busy !== null} onClick={approveEdited}>
                  Confirmer avec ces paramètres
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setEditing(false)}>
                  Annuler la modification
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-2">
              <Button busy={busy === "approve"} disabled={busy !== null} onClick={() => void decide("approve")} className={cx("px-[18px] md:h-[34px] md:text-[14.5px]", (compact || deck) && "flex-1 md:flex-none")}>
                Oui
              </Button>
              <Button variant="outline" busy={busy === "reject"} disabled={busy !== null} onClick={() => void decide("reject")} className={cx("px-[18px] md:h-[34px] md:text-[14.5px]", (compact || deck) && "flex-1 md:flex-none")}>
                Non
              </Button>
              {approval.always ? <AlwaysButton approval={approval} busy={busy} onClick={() => void decide("always")} /> : null}
              {!compact ? <span className="ml-auto text-[12.5px] text-subtle max-sm:hidden">{fmtRelative(approval.createdAt, now)}</span> : null}
            </div>
          )}
        </div>
      ) : (
        <p className={cx("flex min-w-0 items-center gap-2 text-[14px] font-medium", done ? "text-fg" : approval.status === "failed" ? "text-danger" : "text-muted")}>
          <span aria-hidden="true" className="shrink-0">
            {done ? <IconCheck width={16} height={16} strokeWidth={2.4} /> : <IconX width={16} height={16} strokeWidth={2.4} />}
          </span>
          <span className="min-w-0 wrap-anywhere">{approvalOutcome(approval)}</span>
        </p>
      )}

      {error ? (
        <Notice tone="danger" role="alert">
          {error}
        </Notice>
      ) : null}

      <ApprovalDetails
        approval={approval}
        agentName={agentName}
        now={now}
        compact={compact}
        pending={pending}
        overdue={overdue}
        done={done}
        note={note}
        setNote={setNote}
        noteId={noteId}
        editable={pending && editable && !editing}
        editing={editing}
        onEdit={() => {
          setArgsText(JSON.stringify(approval.action?.args ?? {}, null, 2));
          setArgsError(null);
          setEditing(true);
        }}
      />
    </article>
  );
}

function AlwaysButton({ approval, busy, onClick }: { approval: ApprovalView; busy: string | null; onClick: () => void }) {
  return (
    <button
      type="button"
      disabled={busy !== null}
      aria-busy={busy === "always" || undefined}
      onClick={onClick}
      title={`Oui, et ne plus te demander pour : ${approval.always}`}
      aria-label={`Toujours oui pour ${alwaysShort(approval.always ?? "")}`}
      className="inline-flex min-h-11 max-w-full items-center gap-1.5 px-1.5 text-left text-[13.5px] text-muted hover:text-fg disabled:opacity-50 md:min-h-[34px]"
    >
      {busy === "always" ? <Spinner className="h-3.5 w-3.5" /> : null}
      <span className="min-w-0 truncate">Toujours oui</span>
    </button>
  );
}

/** « Détails »: every technical line of the exact action, the agent's reason, the code, the expiry, a note. */
function ApprovalDetails({
  approval,
  agentName,
  now,
  compact,
  pending,
  overdue,
  done,
  note,
  setNote,
  noteId,
  editable,
  editing,
  onEdit,
}: {
  approval: ApprovalView;
  agentName: string;
  now: number;
  compact: boolean;
  pending: boolean;
  overdue: boolean;
  done: boolean;
  note: string;
  setNote: (v: string) => void;
  noteId: string;
  editable: boolean;
  editing: boolean;
  onEdit: () => void;
}) {
  return (
    <details className="group text-[13px]">
      <summary className="inline-flex min-h-11 cursor-pointer list-none items-center gap-1 text-subtle select-none hover:text-fg sm:min-h-7 [&::-webkit-details-marker]:hidden">
        <span>Détails</span>
        <span aria-hidden="true" className="text-[11px] transition group-open:rotate-90">
          ›
        </span>
      </summary>
      <div className="mt-1.5 flex flex-col gap-2.5 rounded-[14px] bg-inset p-3.5 text-muted">
        <ul className="space-y-1 leading-relaxed">
          {approval.lines.map((line, i) => (
            <li key={`${i}-${line.label}`} className="min-w-0 wrap-anywhere">
              <span className="font-medium">{line.label}</span>
              <span> : </span>
              <span className="whitespace-pre-wrap text-fg">{line.value}</span>
            </li>
          ))}
        </ul>
        {approval.reason ? (
          <p className="wrap-anywhere">
            <span className="font-medium">Raison donnée par {agentName}</span> : {approval.reason}
          </p>
        ) : null}
        {approval.status === "failed" && approval.error ? <p className="text-danger wrap-anywhere">{approval.error}</p> : null}
        {done && approval.result ? <p className="wrap-anywhere">{approval.result}</p> : null}
        <p className="text-[12.5px] text-subtle">
          {approval.ruleAction === "sensitive_read" ? "Lecture confidentielle" : (RISK_LABEL[approval.risk] ?? approval.risk)}
          {" · "}
          {pending
            ? overdue
              ? "délai dépassé"
              : `expire ${fmtRelative(approval.expiresAt, now)}`
            : approval.decidedAt
              ? `décidée ${fmtDateTime(approval.decidedAt, now)}${approval.decidedVia ? ` ${VIA_LABEL[approval.decidedVia] ?? ""}` : ""}`
              : `créée ${fmtDateTime(approval.createdAt, now)}`}
          {" · "}code {approval.code}
          {pending && approval.textDecision ? ` (réponds OUI ${approval.code} par message)` : null}
        </p>
        {approval.action && !compact ? (
          <pre className="max-h-64 overflow-auto rounded-xl bg-bg p-3 font-mono text-[12px] leading-relaxed whitespace-pre-wrap text-fg wrap-anywhere">
            {approval.action.toolSlug}
            {"\n"}
            {JSON.stringify(approval.action.args, null, 2)}
          </pre>
        ) : null}
        {pending && !compact && !editing ? (
          <div>
            <label htmlFor={noteId} className="mb-1 block text-[13px] font-medium">
              Une note pour {agentName} (facultatif)
            </label>
            <input
              id={noteId}
              value={note}
              maxLength={500}
              onChange={(e) => setNote(e.target.value)}
              className="h-11 w-full rounded-[14px] border border-line bg-bg px-3.5 text-[14px] text-fg outline-none focus:border-line-strong focus:ring-4 focus:ring-[var(--glow)]"
              placeholder="Ex. pas avant lundi"
            />
          </div>
        ) : null}
        <div className="flex flex-wrap gap-x-4">
          {editable ? (
            <button type="button" className="inline-flex min-h-11 items-center font-semibold text-accent hover:text-accent-hover sm:min-h-8" onClick={onEdit}>
              Modifier les paramètres
            </button>
          ) : null}
          {approval.runId ? (
            <Link to="/runs/$id" params={{ id: approval.runId }} className="inline-flex min-h-11 items-center font-semibold text-accent hover:text-accent-hover sm:min-h-8">
              Voir la mission
            </Link>
          ) : null}
        </div>
      </div>
    </details>
  );
}
